import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { SITE } from "../lib/site";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  useEffect(() => {
    if (user && typeof user === "object") {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Bienvenido, Dr. Franco");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-admin-login" className="relative min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(700px 500px at 20% 20%, rgba(79,163,255,0.18), transparent 60%), radial-gradient(700px 500px at 80% 80%, rgba(30,90,168,0.10), transparent 60%), linear-gradient(180deg, #F8FBFF 0%, #EEF3F9 100%)",
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-brand-deep/60 hover:text-brand-medical mb-5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Volver al sitio
        </Link>

        <div className="rounded-[2rem] glass-strong premium-shadow p-8 md:p-10">
          <div className="flex flex-col items-center text-center mb-7">
            <img src={SITE.logo} alt="Dr. Jairo Franco" className="w-14 h-14 rounded-full ring-1 ring-brand-silver mb-4" />
            <p className="text-xs uppercase tracking-[0.25em] text-brand-medical mb-2 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Acceso administrativo
            </p>
            <h1 className="font-serif text-3xl md:text-4xl text-brand-deep leading-tight">
              Panel del Dr. Franco
            </h1>
            <p className="text-sm text-brand-deep/60 font-light mt-2">
              Inicia sesión para gestionar tus citas.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-brand-medical mb-2">Correo</span>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-deep/40" strokeWidth={1.5} />
                <input
                  type="email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  required
                  className="w-full rounded-2xl bg-white/90 hairline pl-11 pr-4 py-3 text-brand-deep placeholder:text-brand-deep/35 font-light focus:outline-none focus:ring-2 focus:ring-brand-bright/40"
                />
              </div>
            </label>

            <label className="block">
              <span className="block text-xs uppercase tracking-[0.18em] text-brand-medical mb-2">Contraseña</span>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-deep/40" strokeWidth={1.5} />
                <input
                  type="password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full rounded-2xl bg-white/90 hairline pl-11 pr-4 py-3 text-brand-deep placeholder:text-brand-deep/35 font-light focus:outline-none focus:ring-2 focus:ring-brand-bright/40"
                />
              </div>
            </label>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-2.5 border border-red-100">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white px-7 py-3.5 font-medium tracking-wide transition-all premium-shadow disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {loading ? "Verificando..." : "Iniciar sesión"}
            </button>
          </form>

          <p className="mt-6 text-xs text-center text-brand-deep/45">
            Acceso restringido. Tus credenciales están protegidas con cifrado bcrypt.
          </p>
        </div>
      </motion.div>
    </div>
  );
}
